import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SocialTopicAuthor } from "@digital-twins/shared-types";
import { cn } from "@/lib/utils";
import { socialPlatformColorStyle, SOCIAL_PLATFORM_TEXT_CLASS } from "./charts/socialChartColors";
import { topVoices } from "./charts/socialTopicMetrics";
import { SocialPlatformIcon } from "./SocialPlatformIcon";
import { SocialAuthorAvatar } from "./SocialPostTile";
import { SOCIAL_PANEL_BOTTOM_PADDING, SocialTopicPanelHeader } from "./SocialTopicPanelHeader";
import { SocialTopicSlotContext } from "./SocialTopicView";
import { useTopicHighlight } from "./SocialTopicHighlightContext";
import { formatSocialCount, SOCIAL_PLATFORM_LABELS } from "./socialFormat";
import { useSocialTopicAuthors } from "./useSocialTopicAuthors";

interface SocialTopVoicesProps {
  /** Reads the authors; a click sets the author filter on `context.topicFilters`. */
  context: SocialTopicSlotContext;
  /** Defaults to 5. */
  limit?: number;
}

const DEFAULT_LIMIT = 5;

function normalizedHandle(handle: string): string {
  return handle.trim().replace(/^@+/, "").toLowerCase();
}

export function initialsOf(author: SocialTopicAuthor): string {
  const words = (author.displayName ?? "").trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  const source = words[0] ?? author.handle.replace(/^@+/, "");
  return source.slice(0, 2).toUpperCase();
}

/**
 * The top authors under the filters, by reach then posts (posts then reach
 * in posts mode), with followers when a post carries them. A click sets the
 * author filter; a second click on the same author clears it.
 */
export function SocialTopVoices(props: SocialTopVoicesProps) {
  const { context, limit = DEFAULT_LIMIT } = props;
  const { universe, topicFilters, refreshKey } = context;

  const { t, i18n } = useTranslation("app");
  const { setActive } = useTopicHighlight();
  const { metric, author: selectedAuthor } = topicFilters.filters;
  const filters = useMemo(() => topicFilters.facetFilters(), [topicFilters]);
  const { authors } = useSocialTopicAuthors({
    token: universe.token,
    filters,
    sort: metric === "reach" ? "views" : "posts",
    limit,
    refreshKey,
  });

  const voices = useMemo(
    () => (authors ? topVoices(authors.authors, metric, limit) : []),
    [authors, metric, limit],
  );

  const isSelected = (author: SocialTopicAuthor) =>
    selectedAuthor != null &&
    selectedAuthor.platform === author.platform &&
    normalizedHandle(selectedAuthor.handle) === normalizedHandle(author.handle);

  const onSelect = (author: SocialTopicAuthor) => {
    topicFilters.setFilter(
      "author",
      isSelected(author) ? null : { platform: author.platform, handle: author.handle },
    );
  };

  return (
    <section
      aria-label={t("socialListening.topicPage.topVoices.title")}
      className={SOCIAL_PANEL_BOTTOM_PADDING}
      data-testid="social-topic-top-voices"
    >
      <SocialTopicPanelHeader
        title={t("socialListening.topicPage.topVoices.title")}
        trailing={t(`socialListening.topicPage.topVoices.hint.${metric}`)}
      />
      {authors && voices.length === 0 ? (
        <p
          className="px-5 pt-3 text-sm text-muted-foreground"
          data-testid="social-topic-top-voices-empty"
        >
          {t("socialListening.topicPage.topVoices.empty")}
        </p>
      ) : null}
      {voices.length > 0 ? (
        <ul className="px-3 pt-1.5">
          {voices.map((voice, index) => {
            const { author } = voice;
            const detail =
              metric === "reach"
                ? t("socialListening.topicPage.topVoices.posts", { count: voice.secondary })
                : t("socialListening.topicPage.topVoices.views", {
                    value: formatSocialCount(voice.secondary, i18n.language),
                  });
            const sub =
              author.followers != null
                ? t("socialListening.topicPage.topVoices.withFollowers", {
                    detail,
                    followers: t("socialListening.topicPost.followers", {
                      count: author.followers,
                      value: formatSocialCount(author.followers, i18n.language),
                    }),
                  })
                : detail;
            const handle = author.handle.replace(/^@+/, "");
            return (
              <li key={`${author.platform}:${normalizedHandle(author.handle)}`}>
                <button
                  type="button"
                  data-testid={`social-topic-top-voice-${index}`}
                  aria-pressed={isSelected(author) ? "true" : "false"}
                  aria-label={t("socialListening.topicPage.topVoices.filterAria", { handle })}
                  onClick={() => onSelect(author)}
                  onMouseEnter={() =>
                    setActive({
                      kind: "author",
                      author: { platform: author.platform, handle: author.handle },
                    })
                  }
                  onMouseLeave={() => setActive(null)}
                  className={cn(
                    "grid w-full grid-cols-[1.25rem_1.75rem_minmax(0,1fr)_auto] items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    isSelected(author) && "ring-1 ring-primary/40",
                  )}
                >
                  <span className="text-xs tabular-nums text-muted-foreground">{index + 1}</span>
                  <span className="relative">
                    <SocialAuthorAvatar
                      name={author.displayName ?? handle}
                      platform={author.platform}
                      avatarPath={author.authorAvatarPath}
                      initials={initialsOf(author)}
                      size="xs"
                      testId={`social-top-voice-avatar-img-${index}`}
                    />
                    <span
                      className={cn(
                        "absolute -bottom-0.5 -right-0.5 grid size-[15px] place-items-center rounded-full bg-background p-[2px]",
                        SOCIAL_PLATFORM_TEXT_CLASS,
                      )}
                      style={socialPlatformColorStyle(author.platform)}
                      role="img"
                      aria-label={SOCIAL_PLATFORM_LABELS[author.platform]}
                    >
                      <SocialPlatformIcon platform={author.platform} className="size-full" />
                    </span>
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foreground">
                      @{handle}
                    </span>
                    <span className="block truncate text-xs tabular-nums text-muted-foreground">
                      {sub}
                    </span>
                  </span>
                  <span className="text-right text-sm font-semibold tabular-nums text-foreground">
                    {formatSocialCount(voice.primary, i18n.language)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
